import {
  emailRegex,
  iframeTagName,
  phoneNumberRegex,
  stateRegex,
  tempIsoStates,
  zipRegex,
} from 'types/constants';
import { NCRSecurePaymentResponse } from 'types/DTOs';
import { MakeSecurePaymentRequest } from 'types/DTOs/NCRSecurePayment/MakeSecurePaymentRequest';
import { FPInitType } from 'types/DTOs/SiteConfiguration';
import { traceAIEvent } from 'utils';

export const isIframe = (
  input: HTMLElement | null
): input is HTMLIFrameElement => {
  return input !== null && input.tagName === iframeTagName;
};

export const splitExpirationDate = (expDate: string): string[] =>
  expDate.split('/');

//#region FP Additional Inputs Validations
export const generateFpInputErrorMsg = (name: string): string => {
  return `${name} is incomplete`;
};

export const isCardholderFirstNameValid = (
  value: string | undefined
): boolean => {
  if (value === undefined) return true;
  return value.length > 0;
};

export const isCardholderLastNameValid = (
  value: string | undefined
): boolean => {
  if (value === undefined) return true;
  return value.length > 0;
};

export const isFpEmailAddressValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;
  return value.length > 0 && emailRegex.test(value);
};

export const isFpPhoneNumberValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;
  return value.length > 0 && phoneNumberRegex.test(value);
};

export const isFpStreetAddressValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;
  return value ? value.length > 0 : false;
};

export const isFpCityValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;
  return value ? value.length > 0 : false;
};

// TODO: In the future when enhancing State field to dropdown, refer to this package (https://yarnpkg.com/package/iso-3166-2)
export const isFpStateValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;

  const tempValue = value.toUpperCase();
  return (
    tempValue.length > 0 &&
    stateRegex.test(tempValue) &&
    tempIsoStates.includes(tempValue)
  );
};

export const isFpZipCodeValid = (value: string | undefined): boolean => {
  if (value === undefined) return true;
  return value.length > 0 && zipRegex.test(value);
};
//#endregion FP Additional Inputs Validations

export const getFreedomPayIframeSrc = (
  response: NCRSecurePaymentResponse
): string => {
  if (!response) return '';

  const fpIframeDom = new DOMParser().parseFromString(
    response.FreedomPayIFrame || '',
    'text/html'
  );

  return (
    fpIframeDom.getElementsByTagName('iframe')[0].getAttribute('src') || ''
  );
};

export const maskGoogleApplePayCardNumber = (
  request: MakeSecurePaymentRequest,
  paymentType: FPInitType
): string => {
  var result = '';
  var cardNumber = '';

  if (
    request.googleApplePaymentKeyData &&
    request.googleApplePaymentKeyData.attributes
  ) {
    if (
      paymentType === FPInitType.GooglePay &&
      request.googleApplePaymentKeyData.attributes.MaskedCardNumber
    ) {
      cardNumber =
        request.googleApplePaymentKeyData.attributes.MaskedCardNumber;
    }
    if (
      paymentType === FPInitType.ApplePay &&
      request.googleApplePaymentKeyData.attributes.maskedCardNumber
    ) {
      cardNumber =
        request.googleApplePaymentKeyData.attributes.maskedCardNumber;
    }

    result = cardNumber
      ? 'XXXXXXXXXXXX' +
        cardNumber.substring(cardNumber.length - 4, cardNumber.length)
      : '';
  }
  return result;
};

export const removeDashFromPhoneNumber = (value: string) =>
  value.replaceAll('-', '');

export const generateIsoStateString = (value: string) =>
  `US-${value.toUpperCase()}`;


export const is3DSAuthFailed = (data: any): boolean => {
  const enrolled = data?.CmpiLookup?.Enrolled;
  const frictionlessEciFlag = data?.CmpiLookup?.EciFlag;
  const frictionlessCavv = data?.CmpiLookup?.Cavv;
  const frictionlessPares = data?.CmpiLookup?.PAResStatus;
  const validated = data?.Validated;
  const stepUpEciFlag = data?.Payment?.ExtendedData?.ECIFlag;
  const stepUpCavv = data?.Payment?.ExtendedData?.CAVV;

  const logAndTrace = (color: string, message: string) => {
    traceAIEvent(
      {
        message: `[${color} ${message}] data: ${JSON.stringify(data)}`,
      },
      { HasEnrolled: enrolled, EciFlag: frictionlessEciFlag }
    );
  };

  const isValidFrictionless = (eciFlag: string, pares: string, cavv: string) => {
    const validFlags = ["05", "02", "06", "01"];
    const paresStatusValid = pares === "Y" || pares === "A";
    return validFlags.includes(eciFlag) && paresStatusValid && cavv !== "";
  };

  const isInvalidFrictionless = (eciFlag: string, pares: string, cavv?: string) => {
    const invalidFlags = ["07", "00"];
    return (
      invalidFlags.includes(eciFlag) &&
      ["R", "N", "U"].includes(pares) &&
      cavv === ""
    );
  };

  const handleFrictionless = (): boolean => {
    if (isValidFrictionless(frictionlessEciFlag, frictionlessPares, frictionlessCavv)) {
      if (frictionlessPares === "Y") {
        logAndTrace("Green", "Authenticated Transaction");
      } else if (frictionlessPares === "A") {
        logAndTrace("Green", "Attempted Transaction (Stand In)");
      }
      return false; // No failure
    }

    if (isInvalidFrictionless(frictionlessEciFlag, frictionlessPares, frictionlessCavv)) {
      const message =
        frictionlessPares === "R"
          ? "Authentication Rejected Frictionless"
          : frictionlessPares === "N"
          ? "Failed Frictionless Authentication"
          : "Authentication Unavailable Frictionless";
      logAndTrace("Yellow", message);
      return true; // Failure
    }

    return true; // Default failure case
  };

  const handleEnrolled = (): boolean => {
    if (!enrolled) return false; // Skip logic if enrolled is not provided

    if (enrolled === "Y") {
      return handleFrictionless(); // Call handleFrictionless here
    }

    if (["U", "B"].includes(enrolled)) {
      const message =
        enrolled === "U"
          ? "Card not Applicable for Benefits Frictionless"
          : "Bypassed Authentication";
      logAndTrace("Yellow", message);
      return false; // No failure
    }

    return true; // Default failure case if enrolled is neither Y, U, nor B
  };

  const handleStepUp = (): boolean => {
    if (!enrolled && validated) {
      if (
        ["00", "07"].includes(stepUpEciFlag) ||
        stepUpCavv === null ||
        stepUpCavv === ""
      ) {
        logAndTrace("Red", "Failed StepUp Authentication");
        return true; // Failure
      } else {
        logAndTrace("Step-up", "Authenticated Transaction");
        return false; // No failure
      }
    }
    if(!enrolled && !validated){
      logAndTrace("Red", "Failed StepUp Authentication");
      return true;
    }
    return false; // No failure if no step-up validation required
  };

  // Main Logic
  const isFailed = handleEnrolled() || handleStepUp();
  return isFailed;
};